import { Icons } from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import { useInstances } from 'features/db-optimization/v2/hooks/useInstances';
import {
  useDboParams,
  useDboUrlParams,
} from 'features/db-optimization/v2/hooks/useDboParams';
import { buildDboRoute, DboRoute } from 'features/db-optimization/v2/routes';
import { InstanceViewModelV2 } from 'features/db-optimization/v2/types';
import { InstanceIcon } from 'features/db-optimization/v2/components/InstanceIcon';

import { HeaderSelect } from './_components/HeaderSelect';
import { HeaderSelectConfig } from './types';

export const DboInstanceSelect = ({
  useStoredParams = true,
}: {
  useStoredParams?: boolean;
}) => {
  const navigate = useNavigate();
  const routeParams = useDboUrlParams();
  const storedParams = useDboParams();
  const { dbServerId } = useStoredParams ? storedParams : routeParams;
  const { data, isLoading, error, refetch } = useInstances();
  const hasError = !!error;

  const instances: InstanceViewModelV2[] = [
    ...(data?.optimized ?? []),
    ...(data?.other ?? []),
  ];

  const options = instances.map((instance) => ({
    item: instance,
    selected: instance.id === dbServerId,
  }));

  const config: HeaderSelectConfig<InstanceViewModelV2> = {
    options,
    isLoading,
    hasError,
    refetch,
    getItemId: ({ id }) => id,
    getItemName: ({ name }) => name,
    getItemSecondaryText: ({ protocolLabel }) => protocolLabel,
    renderItemIcon: ({ provider }) => (
      <InstanceIcon provider={provider} size={14} />
    ),
    renderTriggerIcon: ({ provider }) => (
      <InstanceIcon provider={provider} size={20} />
    ),
    getSearchableText: ({ name, protocolLabel }) => [name, protocolLabel],
    onSelect: ({ id }) =>
      navigate(
        buildDboRoute(DboRoute.SERVER_DASHBOARD, {
          dbServerId: id,
        })
      ),
    testIdPrefix: 'dbo-instance-chooser',
    searchPlaceholder: 'Enter instance name',
    emptyTitle: 'No instances',
    emptySubtitle: 'Connect an instance to get started',
    notFoundTitle: 'No instances found',
    notFoundSubtitle: 'Adjust your search criteria',
    errorTitle: 'Failed to load instances',
    defaultTriggerLabel: 'Select instance',
    emptyIcon: <Icons.Database size={14} />,
  };

  return <HeaderSelect config={config} />;
};
